import { ScenariosCondition, ScenariosResponse } from '@api/api/scenario';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { parseScenarioData } from '@terminal/widgets/scenario/utils/scenario-table-data/scenario-table-data';
import {
  getHorizonColumns,
  getSummaryColumns,
  ScenarioTableData,
} from '@terminal/widgets/scenario/utils/scenario-table-utils/scenario-table-utils';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useScenarioStore } from '../../hooks/use-scenario-store/useScenarioStore';
import { EmptyTable } from '../empty-table/EmptyTable';
import { ScenarioNoResults } from '../scenario-empty-state/ScenarioNoResults';
import { ScenarioNoAssets } from '../scenario-empty-state/scenario-no-assets/ScenarioNoAssets';
import { ScenarioNoConditions } from '../scenario-empty-state/scenario-no-conditions/ScenarioNoConditions';
import { ScenarioNoFilteredAssets } from '../scenario-empty-state/scenario-no-filtered-assets/ScenarioNoFilteredAssets';
import { ScenarioReadyToRun } from '../scenario-empty-state/scenario-ready-to-run/ScenarioReadyToRun';
import { ScenarioUsageExceeded } from '../scenario-empty-state/scenario-usage-exceeded/ScenarioUsageExceeded';
import { ScenarioLoader } from '../scenario-loader/ScenarioLoader';
import { ScenarioTable } from '../scenario-table/ScenarioTable';
import styles from './ScenarioResults.module.scss';

export interface ScenarioResultsProps {
  activeHorizon: string;
  conditionsCount: number;
  data: ScenariosResponse[];
  disabled: boolean;
  entityTags: string[];
  noAssetsButtonLabel?: string;
  hasError: boolean;
  horizons: string[];
  isLoading: boolean;
  isStreaming?: boolean;
  isSummary: boolean;
  lastConditions?: ScenariosCondition[];
  onAbort: () => void;
  onClearScenarioTable: () => void;
  onEditAssets: () => void;
  onAddConditions?: () => void;
  onRunConditions: () => void;
  onRowClick: (item: ScenarioTableData) => void;
}

export const ScenarioResults = ({
  activeHorizon,
  conditionsCount,
  data,
  disabled,
  entityTags,
  noAssetsButtonLabel,
  hasError,
  horizons,
  isLoading,
  isStreaming,
  isSummary,
  lastConditions,
  onAbort,
  onClearScenarioTable,
  onEditAssets,
  onAddConditions,
  onRowClick,
  onRunConditions,
}: ScenarioResultsProps) => {
  const { t } = useTranslation('scenario');

  const hasActiveAssetFilters = useScenarioStore(
    state => !!state.filterGroups.ASSET_FILTERS.activeFilterOptions.length
  );
  const hasExceededUsageLimit = useScenarioStore(
    state => state.hasExceededUsageLimit
  );

  const parsedData = useMemo(
    () => parseScenarioData({ data, horizons, activeHorizon }),
    [data, horizons, activeHorizon]
  );
  const columns = useMemo(
    () => (isSummary ? getSummaryColumns(t) : getHorizonColumns(t)),
    [isSummary, t]
  );

  const hasData = data.some(item => item.predictions.length > 0);
  const hasNoAssets = !entityTags.length;
  const hasNoConditions = !conditionsCount;

  if (isLoading) {
    return (
      <ScenarioLoader
        countEntities={entityTags.length}
        onAbort={onAbort}
        conditionsCount={conditionsCount}
      />
    );
  }

  if (hasExceededUsageLimit) {
    return (
      <EmptyTable entityTags={entityTags}>
        <ScenarioUsageExceeded
          assetsCount={entityTags.length}
          conditionsCount={conditionsCount}
        />
      </EmptyTable>
    );
  }

  if (hasNoAssets) {
    if (hasActiveAssetFilters) {
      return (
        <ScenarioNoFilteredAssets
          onAbort={onAbort}
          onClearScenarioTable={onClearScenarioTable}
        />
      );
    } else {
      return (
        <>
          <ScenarioNoAssets
            label={noAssetsButtonLabel}
            onClick={onEditAssets}
            hasNoConditions={hasNoConditions}
          />
        </>
      );
    }
  }

  if (!lastConditions?.length || disabled) {
    const hasConditions = conditionsCount > 0;

    return (
      <EmptyTable entityTags={entityTags}>
        {hasConditions ? (
          <ScenarioReadyToRun
            assetsCount={entityTags.length}
            conditionsCount={conditionsCount}
            runConditionsOnClick={onRunConditions}
          />
        ) : (
          <ScenarioNoConditions addConditionsOnClick={onAddConditions} />
        )}
      </EmptyTable>
    );
  }

  if (!hasData) {
    // Early chunks can carry no prediction for the active horizon, so an
    // in-flight run isn't evidence that the scenario has no results.
    if (isStreaming) {
      return (
        <ScenarioLoader
          countEntities={entityTags.length}
          onAbort={onAbort}
          conditionsCount={conditionsCount}
        />
      );
    }

    return (
      <EmptyTable entityTags={entityTags}>
        <ScenarioNoResults />
      </EmptyTable>
    );
  }

  return (
    <div
      className={clsx(styles.scenarioResultsRoot, {
        [styles.disabled]: disabled,
      })}
      data-testid="scenario-results"
    >
      <ErrorBoundaryWrapper showBorder={false}>
        <ScenarioTable
          data={parsedData}
          columns={columns}
          hasError={hasError}
          isStreaming={isStreaming}
          onRowClick={onRowClick}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
